const expertiseAreas = [
  {
    label: 'Architecture',
    title: 'Clean Architecture & Modern Design',
    description:
      'We apply modern software engineering practices including clean architecture and component-based design to create systems that are scalable, testable, and easy to maintain over the long term.',
    isDark: false,
  },
  {
    label: 'Performance',
    title: 'Performance Optimization & Stability',
    description:
      'Every system we build is optimized for speed, responsiveness, and reliability. We employ rigorous profiling and tuning techniques to ensure consistent high performance under real-world conditions.',
    isDark: true,
  },
  {
    label: 'Experience',
    title: 'User-Centered Development',
    description:
      'Usability sits at the core of our engineering process. We design and build with the end user in mind, ensuring intuitive interactions and polished experiences across every touchpoint.',
    isDark: false,
  },
];

function CardField({ isDark }: { isDark: boolean }) {
  return (
    <div
      className={`w-full aspect-[5/4] rounded-2xl ${
        isDark
          ? 'bg-background-900'
          : 'bg-background-100'
      } relative overflow-hidden`}
    >
      <div
        className={`absolute inset-0 ${
          isDark
            ? 'bg-gradient-to-br from-background-800/50 to-transparent'
            : 'bg-gradient-to-br from-background-200/60 to-transparent'
        }`}
      />
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-px ${
          isDark ? 'bg-background-600/30' : 'bg-background-300/40'
        }`}
      />
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-3/4 ${
          isDark ? 'bg-background-600/30' : 'bg-background-300/40'
        }`}
      />
      <div
        className={`absolute top-1/3 left-1/4 w-1/2 h-px ${
          isDark ? 'bg-background-600/20' : 'bg-background-300/30'
        }`}
      />
      <div
        className={`absolute bottom-1/3 right-1/4 w-1/3 h-px ${
          isDark ? 'bg-background-600/20' : 'bg-background-300/30'
        }`}
      />
    </div>
  );
}

export default function ExpertiseSection() {
  return (
    <section
      className="relative w-full bg-background-50 py-20 md:py-28 px-6 md:px-12 lg:px-20"
      aria-label="Areas of expertise"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-16 mb-16 md:mb-20">
          <div className="flex-shrink-0">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              What We Do
            </h2>
          </div>
          <div className="flex-1 lg:pb-1">
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-xl font-body">
              Our team follows modern software engineering practices to deliver
              systems that are reliable, scalable, and built to last.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {expertiseAreas.map((area) => (
            <div key={area.label} className="flex flex-col">
              <CardField isDark={area.isDark} />
              <div className="mt-5">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-label text-xs font-medium tracking-wide uppercase">
                  {area.label}
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                {area.title}
              </h3>
              <p className="mt-3 text-foreground-600 text-sm md:text-base leading-relaxed font-body">
                {area.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}